// WHAT IS THIS FILE? — the 9 x 5 peer ladder, and the anchored patterns that recognise a rung.
//
// The nine houses in `bm_fund_houses` (45 rows = 9 houses x 5 levels) ARE the CBAM benchmark:
// the equal-weight mean of the nine at level N is `CBAM {N} Benchmark`, verified to 0.0033pp at
// level V against real FE data (FE-EXPORT-CLASSIFICATION-2026-07.md §1.3).
//
// ⚠️ TWO STANDING RULES, both learned the hard way:
//
//  1. **Scottish Widows Horizon is IN.** It is `is_active = false` in `bm_fund_houses` (the flag
//     governs the ALLOCATION side, not the PERFORMANCE side). Filter it out and the level-V mean
//     moves from -0.98 to -0.90 and reconciliation breaks by 0.08pp. Santander is likewise IN;
//     excluding it gives -1.03 against a published -0.98.
//
//  2. **The house is identified by an ANCHORED prefix + an exact rung token.** No fuzzy matching,
//     ever. "Invesco Summit Growth 3" is a peer; "Invesco High Yield (UK) Z Acc" and
//     "Invesco Money (UK) No Trail Acc" are HOLDINGS that happen to share a manager. A pattern
//     loose enough to catch the first two would mint two phantom benchmark constituents.
//
// Rung vocabularies differ per house — that is the whole difficulty. 7IM speaks in adjectives,
// BlackRock in equity percentages, L&G in an offset integer scale (3..7), Vanguard in equity
// percentages that are NOT BlackRock's. The mapping below is the CIO's, not inferred from names.

import type { PeerHouseId, RiskLevel } from "./types";

export const RISK_LEVELS: readonly RiskLevel[] = [1, 2, 3, 4, 5] as const;

/** Roman numerals as FE writes them on the CBAM benchmark ladder, indexed by level. */
export const ROMAN_BY_LEVEL: Record<RiskLevel, string> = {
  1: "I",
  2: "II",
  3: "III",
  4: "IV",
  5: "V",
};

export const LEVEL_BY_ROMAN: Record<string, RiskLevel> = {
  I: 1,
  II: 2,
  III: 3,
  IV: 4,
  V: 5,
};

interface PeerHouseDef {
  id: PeerHouseId;
  /** Human label. Matches `bm_fund_houses.name` where the two agree. */
  label: string;
  /**
   * Every spelling of the house prefix seen in the wild. FE has renamed series before; a second
   * spelling here is cheaper than a silent miss that drops a constituent and breaks F9.
   */
  prefixes: string[];
  /** rung token per level, in that house's own vocabulary. */
  products: Record<RiskLevel, string>;
}

const HOUSE_DEFS: readonly PeerHouseDef[] = [
  {
    id: "7im_aap",
    label: "7IM AAP",
    prefixes: ["7IM AAP"],
    products: {
      1: "Cautious",
      2: "Moderately Cautious",
      3: "Balanced",
      4: "Moderately Adventurous",
      5: "Adventurous",
    },
  },
  {
    id: "aviva_ai_ma_plus",
    label: "Aviva AI Multi-Asset Plus",
    // FE writes "Aviva Inv AI Multi-asset Plus"; the DB says "Aviva Multi-Asset Plus".
    prefixes: [
      "Aviva Inv AI Multi-asset Plus",
      "Aviva Investors AI Multi-asset Plus",
      "Aviva AI Multi-asset Plus",
    ],
    products: { 1: "I", 2: "II", 3: "III", 4: "IV", 5: "V" },
  },
  {
    id: "blackrock_consensus",
    label: "BlackRock Consensus",
    // `bm_fund_houses` spells it "Blackrock"; FE spells it "BlackRock". Matching is case-blind.
    prefixes: ["BlackRock Consensus"],
    products: { 1: "35", 2: "60", 3: "70", 4: "85", 5: "100" },
  },
  {
    id: "invesco_summit_growth",
    label: "Invesco Summit Growth",
    prefixes: ["Invesco Summit Growth"],
    products: { 1: "1", 2: "2", 3: "3", 4: "4", 5: "5" },
  },
  {
    id: "lg_multi_index",
    label: "L&G Multi-Index",
    prefixes: ["L&G Multi-Index", "Legal & General Multi-Index"],
    products: { 1: "3", 2: "4", 3: "5", 4: "6", 5: "7" },
  },
  {
    id: "liontrust_ma_blended",
    label: "Liontrust MA Blended",
    prefixes: ["Liontrust MA Blended", "Liontrust Multi-Asset Blended"],
    products: {
      1: "Reserve",
      2: "Moderate",
      3: "Intermediate",
      4: "Progressive",
      5: "Growth",
    },
  },
  {
    id: "santander_atlas",
    label: "Santander Atlas",
    prefixes: ["Santander Atlas Portfolio", "Santander Atlas"],
    products: { 1: "3", 2: "4", 3: "5", 4: "6", 5: "7" },
  },
  {
    id: "sw_horizon_ma",
    // The same house appears as "Embark Horizon" in `ba_benchmark_providers` and
    // "Scottish Widows Horizon" in `bm_fund_houses`. Both spellings are accepted.
    label: "Scottish Widows Horizon Multi-Asset",
    prefixes: [
      "Scottish Widows Horizon Multi-Asset",
      "SW Horizon Multi-Asset",
      "Embark Horizon Multi-Asset",
    ],
    products: { 1: "1", 2: "2", 3: "3", 4: "4", 5: "5" },
  },
  {
    id: "vanguard_lifestrategy",
    label: "Vanguard LifeStrategy",
    prefixes: ["Vanguard LifeStrategy", "Vanguard Life Strategy"],
    products: {
      1: "20% Equity",
      2: "40% Equity",
      3: "60% Equity",
      4: "80% Equity",
      5: "100% Equity",
    },
  },
] as const;

export const PEER_HOUSES = HOUSE_DEFS;

export const PEER_HOUSE_IDS: readonly PeerHouseId[] = HOUSE_DEFS.map((h) => h.id);

export const PEER_HOUSE_LABEL: Record<PeerHouseId, string> = HOUSE_DEFS.reduce(
  (acc, h) => {
    acc[h.id] = h.label;
    return acc;
  },
  {} as Record<PeerHouseId, string>,
);

/** 9 houses x 5 levels. The count a complete export must hit. */
export const PEER_CELL_COUNT = HOUSE_DEFS.length * RISK_LEVELS.length; // 45

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Build a whitespace-tolerant, anchored pattern for `${prefix} ${product}`.
 *
 * The trailing boundary is `(?![\w%.])` rather than `\b` because several rungs END in a digit or
 * a `%`, where `\b` gives the wrong answer:
 *   - "Consensus 35"  must not match "Consensus 350"  → `\b` after "35" would allow it? no, but
 *   - "Plus I"        must not match "Plus II"        → `\b` handles this, and so does the lookahead
 *   - "LifeStrategy 100% Equity" ends in a letter, but "Multi-Index 3" ends in a digit and is
 *     followed by " in GB" or end-of-string; `(?![\w%.])` is correct in every one of those cases.
 *
 * A trailing remainder IS allowed — FE leaves share-class tokens on some rows and strips them from
 * others between pulls (TRAP 6): the same export carries both "Invesco Summit Growth 1 (UK)" and
 * "Invesco Summit Growth 2 (UK) No Trail Acc". Both are the same rung.
 */
function rungPattern(prefix: string, product: string): RegExp {
  const p = escapeRegex(prefix).replace(/\s+/g, "\\s+");
  const t = escapeRegex(product).replace(/\s+/g, "\\s+");
  return new RegExp(`^${p}\\s+${t}(?![\\w%.])`, "i");
}

export interface PeerRung {
  house: PeerHouseId;
  houseLabel: string;
  level: RiskLevel;
  product: string;
  /** This entry's own spelling: `${prefix} ${product}`. */
  canonicalName: string;
  /** True for the entry built from the house's FIRST (primary) prefix spelling. */
  isPrimary: boolean;
  pattern: RegExp;
}

/**
 * All 45 rungs, flattened, with one entry per accepted prefix spelling.
 *
 * Sorted by descending pattern-source length so that when two patterns could both fire, the more
 * specific one is tried first. With anchored prefixes and the `(?![\w%.])` boundary this should
 * never actually be load-bearing — and `classifyCombinedExport` asserts it isn't by reporting a
 * `row_matched_multiple_classes` warning if two rungs ever match one name.
 */
export const PEER_RUNGS: readonly PeerRung[] = HOUSE_DEFS.flatMap((house) =>
  RISK_LEVELS.flatMap((level) =>
    house.prefixes.map((prefix, i) => ({
      house: house.id,
      houseLabel: house.label,
      level,
      product: house.products[level],
      canonicalName: `${prefix} ${house.products[level]}`,
      isPrimary: i === 0,
      pattern: rungPattern(prefix, house.products[level]),
    })),
  ),
)
  .slice()
  .sort((a, b) => b.pattern.source.length - a.pattern.source.length);

/**
 * The 45 canonical FE names — one per (house, level), using each house's primary spelling.
 * This is the roster a complete export must satisfy, and the shape tests assert against.
 */
export function canonicalPeerNames(): PeerRung[] {
  return PEER_RUNGS.filter((r) => r.isPrimary);
}

/** Every (house, level) cell, for naming what is missing. */
export function allPeerCells(): { house: PeerHouseId; houseLabel: string; level: RiskLevel }[] {
  return HOUSE_DEFS.flatMap((h) =>
    RISK_LEVELS.map((level) => ({ house: h.id, houseLabel: h.label, level })),
  );
}

export function peerCellKey(house: PeerHouseId, level: RiskLevel): string {
  return `${house}@${level}`;
}

// ----------------------------------------------------------------------------
// External comparators
// ----------------------------------------------------------------------------

/**
 * AFI. `portfolio_benchmarks` carries secondary AFI rows at levels 2/3/4/5 for both P and R, and
 * NO secondary row at level 1 for either — level 1 has no AFI comparator at all.
 *
 * Level 4 is `AFI Balanced:Aggressive 50:50`, which FE does NOT supply as a series. It is
 * COMPUTED as (Balanced + Aggressive) / 2 — see AfiBlendCheck.
 */
export const AFI_LEVEL: Record<string, RiskLevel> = {
  cautious: 2,
  balanced: 3,
  aggressive: 5,
};

/** ARC MPS bands, level-matched per `services/reporting-compute/benchmarks.py` LEVEL_ARC. */
export const ARC_BAND_LEVEL: Record<string, RiskLevel> = {
  "0-20": 1,
  "20-40": 2,
  "40-60": 3,
  "60-80": 4,
  "80-100": 5,
};

export const ARC_BAND_BY_LEVEL: Record<RiskLevel, string> = {
  1: "0-20",
  2: "20-40",
  3: "40-60",
  4: "60-80",
  5: "80-100",
};
